import type { CollectiblesConfig } from '../config/collectibles.config';
import { overlapsX, type Aabb } from '../physics/Aabb';
import { CollectibleEffect, type CollectibleDefinition } from './CollectibleDefinition';

/** Per-run magnet of the character – the perk (CharacterPerks in character.config). */
export interface MagnetModifiers {
  /** Permanent magnet radius (m, 0 = none). */
  readonly magnetRadius: number;
  /** Bottles the permanent magnet pulls per bottle row of a neighbouring lane. */
  readonly neighbourLaneBottleLimit: number;
}

export const NO_MAGNET_MODIFIERS: MagnetModifiers = { magnetRadius: 0, neighbourLaneBottleLimit: 0 };

/** What the magnet looks at – a CollectibleInstance. */
export interface MagnetTarget {
  readonly definition: CollectibleDefinition;
  readonly spawnPoint: { readonly lane: number };
  readonly trackCollider: Readonly<Aabb>;
}

/** The bottle row the runner is passing in one lane. */
interface BottleRow {
  /** Track z of the row's furthest collectible seen so far. */
  lastZ: number;
  /** Bottles the permanent magnet pulled out of this row. */
  pulled: number;
}

/**
 * Bottle magnet: the magnet power-up pulls every bottle within its radius; the character's
 * permanent magnet leaves the runner's own lane to the normal pickup and pulls at most
 * `neighbourLaneBottleLimit` bottles per row of a neighbouring lane – the first ones it reaches,
 * the rest of the row needs a lane change. Rows are tracked per lane while the runner passes
 * them (any lane), so leaving and re-entering a neighbouring lane does not restart a row.
 * Pulled bottles count at once (RunDirector); the flight to the runner is presentation only.
 */
export class CollectibleMagnet {
  private characterRadius = 0;
  private rowLimit = 0;
  private powerUpSeconds = 0;
  private readonly rows = new Map<number, BottleRow>();

  constructor(private readonly config: CollectiblesConfig['magnet']) {}

  /** Largest active radius (m, 0 = no magnet) – the CollisionSystem gathers candidates this far along the track. */
  get radius(): number {
    return Math.max(this.characterRadius, this.powerUpSeconds > 0 ? this.config.radius : 0);
  }

  get powerUpActive(): boolean {
    return this.powerUpSeconds > 0;
  }

  /** Starts a new run with the character's permanent magnet; an active power-up ends. */
  reset(modifiers: MagnetModifiers = NO_MAGNET_MODIFIERS): void {
    this.characterRadius = Math.max(0, modifiers.magnetRadius);
    this.rowLimit = Math.max(0, modifiers.neighbourLaneBottleLimit);
    this.powerUpSeconds = 0;
    this.rows.clear();
  }

  /** Magnet power-up picked up (re-pickup restarts the duration). */
  activatePowerUp(): void {
    this.powerUpSeconds = this.config.duration;
  }

  update(dt: number): void {
    this.powerUpSeconds = Math.max(0, this.powerUpSeconds - dt);
  }

  /**
   * The collectibles pulled in this step. `nearby`: untouched collectibles around the runner along
   * the track (all lanes, ascending z – CollisionSystem). Every one of them is registered with its
   * lane's row, so the row limit holds no matter which lane the runner is in.
   */
  pull<T extends MagnetTarget>(runner: Readonly<Aabb>, nearby: readonly T[], out: T[] = []): T[] {
    out.length = 0;
    const powerUpRadius = this.powerUpSeconds > 0 ? this.config.radius : 0;
    for (const item of nearby) {
      const box = item.trackCollider;
      // Helmets and power-ups count for the row (they replaced one of its bottles) but are never pulled.
      const row = this.rowAt(item.spawnPoint.lane, (box.minZ + box.maxZ) / 2);
      if (!isMagnetic(item.definition)) continue;
      if (inMagnetRange(runner, box, powerUpRadius)) {
        out.push(item);
      } else if (row.pulled < this.rowLimit && !overlapsX(runner, box) && inMagnetRange(runner, box, this.characterRadius)) {
        // Not overlapping sideways = not in the runner's own lane; the radius excludes lanes further away.
        row.pulled++;
        out.push(item);
      }
    }
    return out;
  }

  /** Row of a lane at track z: a gap larger than `rowGap` behind the last seen collectible starts a new row. */
  private rowAt(lane: number, z: number): BottleRow {
    const row = this.rows.get(lane);
    if (row && z - row.lastZ <= this.config.rowGap) {
      row.lastZ = Math.max(row.lastZ, z);
      return row;
    }
    const next = { lastZ: z, pulled: 0 };
    this.rows.set(lane, next);
    return next;
  }
}

/** A magnet pulls bottle pickups only – no helmets, no power-ups, nothing marked `magnetic: false` (golden bottle). */
export function isMagnetic(definition: CollectibleDefinition): boolean {
  return definition.magnetic ?? definition.effect === CollectibleEffect.AddBottles;
}

/**
 * True when the centre of a pickup volume lies within `radius` of the runner's collider centre.
 * Solid obstacles in between are ignored (kept simple, like the power-up in other runners).
 */
export function inMagnetRange(runner: Readonly<Aabb>, pickup: Readonly<Aabb>, radius: number): boolean {
  if (radius <= 0) return false;
  const dx = (runner.minX + runner.maxX - pickup.minX - pickup.maxX) / 2;
  const dy = (runner.minY + runner.maxY - pickup.minY - pickup.maxY) / 2;
  const dz = (runner.minZ + runner.maxZ - pickup.minZ - pickup.maxZ) / 2;
  return dx * dx + dy * dy + dz * dz <= radius * radius;
}
